import { useState, useMemo } from 'react'
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import {
  authService,
  firebaseLogout,
  useAuthStore,
} from '@lipaflow/shared'
import {
  HomeIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChartBarSquareIcon,
  EllipsisHorizontalCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { AmaniIndicator } from '../ui/AmaniAvatar'
import { ImpersonationBanner } from '../ui/ImpersonationBanner'
import NotificationBell from '../ui/NotificationBell'
import DarkModeToggle from '../ui/DarkModeToggle'
import CommandPalette from '../ui/CommandPalette'
import { useNavigation, type NavItem } from '../../lib/useNavigation'
import { PageTitleProvider } from '../../lib/PageTitleProvider'
import { usePageTitleContext } from '../../lib/pageTitle'

export default function DashboardLayout() {
  return (
    <PageTitleProvider>
      <DashboardLayoutInner />
    </PageTitleProvider>
  )
}

function DashboardLayoutInner() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['People', 'Finance', 'Communication'])
  const { user, tenant, logout, isImpersonating, stopImpersonation } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { navigation } = useNavigation()

  const handleLogout = async () => {
    if (isImpersonating) {
      stopImpersonation()
      navigate('/login')
      return
    }

    try {
      await authService.logout()
    } catch {
      // Continue client logout even when backend token revocation fails.
    }
    await firebaseLogout()
    logout()
    navigate('/login')
  }

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((g) => g !== groupName)
        : [...prev, groupName]
    )
  }

  // Get tenant brand color or use default Serenity Teal
  const brandColor = tenant?.brand_color || '#006D77'
  const logoUrl = tenant?.logo_url

  const renderNavItem = (item: NavItem, isMobile = false) => {
    const hasChildren = 'children' in item && item.children
    const isExpanded = expandedGroups.includes(item.name)

    if (hasChildren) {
      return (
        <div key={item.name} className="space-y-0.5">
          <button
            onClick={() => toggleGroup(item.name)}
            className="w-full flex items-center justify-between px-3 py-2 text-ink-faint hover:text-ink-muted transition-colors duration-fast ease-standard"
          >
            <span className="font-outfit text-overline uppercase">
              {item.name}
            </span>
            <ChevronDownIcon
              className={clsx(
                'h-4 w-4 transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
            />
          </button>
          {isExpanded && (
            <div className="space-y-0.5">
              {item.children!.map((child) => (
                <SidebarLink
                  key={child.name}
                  to={child.href}
                  end={child.href === '/'}
                  icon={child.icon}
                  label={child.name}
                  onNavigate={isMobile ? () => setSidebarOpen(false) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      )
    }

    return (
      <SidebarLink
        key={item.name}
        to={item.href!}
        end={item.href === '/'}
        icon={item.icon}
        label={item.name}
        onNavigate={isMobile ? () => setSidebarOpen(false) : undefined}
      />
    )
  }

  const userInitial = user?.full_name?.charAt(0) || 'U'
  const roleLabel = user?.role?.replace(/_/g, ' ')

  return (
    <div className="min-h-screen bg-canvas">
      <CommandPalette />
      <ImpersonationBanner />
      {/* Mobile sidebar overlay */}
      <div
        className={clsx(
          'fixed inset-0 z-50 lg:hidden transition-opacity duration-300',
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        <div
          className="fixed inset-0 bg-scrim/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={clsx(
            'fixed inset-y-0 left-0 w-72 bg-surface border-r border-hairline shadow-overlay',
            'transition-transform duration-normal ease-standard',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <SidebarBrand
            logoUrl={logoUrl}
            tenantName={tenant?.name}
            shortName={tenant?.short_name}
            brandColor={brandColor}
            actionSlot={
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-ink-faint hover:text-ink rounded-button hover:bg-surface-alt transition-colors duration-fast"
                aria-label="Close navigation"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            }
          />
          <nav className="flex-1 px-3 py-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => renderNavItem(item, true))}
          </nav>
          <SidebarUserSection
            userInitial={userInitial}
            userName={user?.full_name}
            userRole={roleLabel}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-surface border-r border-hairline">
          <SidebarBrand
            logoUrl={logoUrl}
            tenantName={tenant?.name}
            shortName={tenant?.short_name}
            brandColor={brandColor}
          />
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => renderNavItem(item, false))}
          </nav>
          <div className="px-4 py-3 border-t border-hairline">
            <AmaniIndicator status="online" />
          </div>
          <SidebarUserSection
            userInitial={userInitial}
            userName={user?.full_name}
            userRole={roleLabel}
            tenantName={tenant?.name}
            onLogout={handleLogout}
          />
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        <TopBar
          onOpenSidebar={() => setSidebarOpen(true)}
          userInitial={userInitial}
          fallbackPath={location.pathname}
          navigation={navigation}
        />

        {/* Page content */}
        <main className="px-4 sm:px-6 py-8 pb-24 lg:pb-10 max-w-container mx-auto space-y-8">
          <Outlet />
        </main>

        <footer className="px-4 sm:px-6 py-6 border-t border-hairline">
          <div className="max-w-container mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="font-inter text-caption text-ink-faint">
              Powered by LipaFlow
            </p>
            <p className="font-inter text-caption text-ink-faint">
              Conversation, Not Confrontation.
            </p>
          </div>
        </footer>
      </div>

      <MobileBottomNav />
    </div>
  )
}

// ── Sidebar sub-components ────────────────────────────────────────────

interface SidebarLinkProps {
  to: string
  end?: boolean
  icon?: React.ElementType
  label: string
  onNavigate?: () => void
}

function SidebarLink({ to, end, icon: Icon, label, onNavigate }: SidebarLinkProps) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        clsx(
          'group relative flex items-center gap-3 px-3 py-2.5 rounded-button font-inter text-body-sm',
          'transition-colors duration-fast ease-standard',
          isActive
            ? 'bg-accent-soft text-accent font-medium'
            : 'text-ink-muted hover:bg-surface-alt hover:text-ink'
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={clsx(
              'absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full transition-all duration-fast',
              isActive ? 'bg-accent' : 'bg-transparent group-hover:bg-hairline'
            )}
            aria-hidden
          />
          {Icon && <Icon className="h-5 w-5 shrink-0" />}
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  )
}

interface SidebarBrandProps {
  logoUrl?: string | null
  tenantName?: string | null
  shortName?: string | null
  brandColor: string
  actionSlot?: React.ReactNode
}

function SidebarBrand({ logoUrl, tenantName, shortName, brandColor, actionSlot }: SidebarBrandProps) {
  return (
    <div className="flex h-16 items-center justify-between gap-2 px-4 border-b border-hairline">
      <Link to="/" className="flex items-center gap-2 min-w-0">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={tenantName || 'Home'}
            className="h-8 max-w-[180px] object-contain"
          />
        ) : (
          <>
            <span
              className="inline-flex h-9 w-9 items-center justify-center rounded-chip text-white font-outfit font-semibold text-body-sm shrink-0"
              style={{ backgroundColor: brandColor }}
              aria-hidden
            >
              {(shortName || 'L').charAt(0).toUpperCase()}
            </span>
            <span
              className="font-outfit text-h4 font-semibold truncate"
              style={{ color: brandColor }}
            >
              {shortName || 'LipaFlow'}
            </span>
          </>
        )}
      </Link>
      {actionSlot}
    </div>
  )
}

interface SidebarUserSectionProps {
  userInitial: string
  userName?: string | null
  userRole?: string | null
  tenantName?: string | null
  onLogout: () => void
}

function SidebarUserSection({
  userInitial,
  userName,
  userRole,
  tenantName,
  onLogout,
}: SidebarUserSectionProps) {
  return (
    <div className="p-4 border-t border-hairline">
      {tenantName && (
        <div className="text-caption font-medium text-ink-faint mb-2 truncate">
          {tenantName}
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
          <span className="font-outfit font-semibold text-body-sm text-on-accent">
            {userInitial}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-inter text-body-sm font-medium text-ink truncate">
            {userName || 'Signed in'}
          </p>
          <p className="font-inter text-caption text-ink-muted capitalize truncate">
            {userRole || ''}
          </p>
        </div>
        <button
          onClick={onLogout}
          className="p-2 text-ink-faint hover:text-danger rounded-button hover:bg-danger-soft transition-colors duration-fast"
          title="Sign out"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

// ── Top bar ───────────────────────────────────────────────────────────

interface TopBarProps {
  onOpenSidebar: () => void
  userInitial: string
  fallbackPath: string
  navigation: NavItem[]
}

function TopBar({ onOpenSidebar, userInitial, fallbackPath, navigation }: TopBarProps) {
  const { title } = usePageTitleContext()

  const derivedTitle = useMemo(() => {
    if (title) return title
    if (fallbackPath === '/') return 'Dashboard'
    const flat = navigation.flatMap<{ name: string; href?: string }>((item) =>
      'children' in item && item.children ? item.children : [item]
    )
    const match = flat.find(
      (it) => it.href && (it.href === fallbackPath || fallbackPath.startsWith(it.href + '/'))
    )
    return match?.name ?? 'Dashboard'
  }, [title, fallbackPath, navigation])

  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

  const openCommandPalette = () => {
    if (typeof window === 'undefined') return
    const event = new KeyboardEvent('keydown', {
      key: 'k',
      code: 'KeyK',
      metaKey: isMac,
      ctrlKey: !isMac,
      bubbles: true,
    })
    window.dispatchEvent(event)
  }

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-hairline bg-surface/80 backdrop-blur px-4 sm:px-6">
      <button
        className="lg:hidden p-2 -ml-2 text-ink-muted hover:text-ink rounded-button hover:bg-surface-alt transition-colors duration-fast"
        onClick={onOpenSidebar}
        aria-label="Open navigation"
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      <div className="min-w-0 flex-1">
        <h2 className="font-outfit text-h4 text-ink truncate">
          {derivedTitle}
        </h2>
      </div>

      <button
        type="button"
        onClick={openCommandPalette}
        className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-button bg-surface-alt text-ink-muted hover:text-ink transition-colors duration-fast"
        title="Command palette"
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
        <span className="font-inter text-body-sm">Search</span>
        <span className="ml-3 hidden lg:inline font-mono text-caption text-ink-faint">
          {isMac ? '⌘K' : 'Ctrl K'}
        </span>
      </button>

      <div className="flex items-center gap-1">
        <DarkModeToggle />
        <NotificationBell />
      </div>

      <div className="lg:hidden w-9 h-9 rounded-full bg-accent flex items-center justify-center">
        <span className="font-outfit font-semibold text-body-sm text-on-accent">
          {userInitial}
        </span>
      </div>
    </header>
  )
}

// ── Mobile Bottom Tab Bar ────────────────────────────────────────────
function MobileBottomNav() {
  const location = useLocation()

  const tabs = [
    { label: 'Home', path: '/', matchPaths: ['/'], icon: HomeIcon },
    { label: 'Payments', path: '/claims', matchPaths: ['/claims', '/payment-plans', '/pledges', '/balance-upload'], icon: BanknotesIcon },
    { label: 'Sessions', path: '/conversations', matchPaths: ['/conversations'], icon: ChatBubbleLeftRightIcon },
    { label: 'Reports', path: '/reports', matchPaths: ['/reports'], icon: ChartBarSquareIcon },
    { label: 'More', path: '/settings', matchPaths: ['/settings'], icon: EllipsisHorizontalCircleIcon },
  ]

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-hairline bg-surface lg:hidden">
      <div className="flex items-center justify-around h-16">
        {tabs.map((tab) => {
          const isActive =
            tab.matchPaths.some((p) =>
              p === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(p)
            )
          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={`flex flex-col items-center justify-center flex-1 h-full text-xs ${
                isActive
                  ? 'text-accent'
                  : 'text-ink-faint'
              }`}
            >
              <tab.icon className={`h-5 w-5 mb-1 ${isActive ? 'text-accent' : ''}`} />
              {tab.label}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
